import { useState, useEffect } from 'react';
import { useAddBondMutation, useUpdateBondMutation } from 'redux/features/employeeBondApi';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'lib/dayjs';
import { usersApi } from 'redux/features/users/userApi';
import Icon from '../../../components/AppIcon';

const AddBondModal = ({ isOpen, onClose, employee, bond, mode, onBondCreated }) => {
  console.log({ isOpen });
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    user_id: null,
    start_date: '',
    end_date: '',
    signed_date: '',
    is_renew: false,
    status: 'ACTIVE',
  });
  const [addBond, { isLoading: isAdding }] = useAddBondMutation();
  const [updateBond, { isLoading: isUpdating }] = useUpdateBondMutation();

  const isRenew = mode === 'renew';
  const isEdit = mode === 'edit';

  useEffect(() => {
    if (isOpen) {
      if (mode === 'renew' && employee) {
        setFormData({
          user_id: employee.id,
          start_date: '',
          end_date: '',
          signed_date: '',
          is_renew: true,
          status: 'ACTIVE',
        });
      } else if (bond) {
        setFormData({
          user_id: bond.user_id,
          start_date: bond.start_date ? dayjs(bond.start_date).format('YYYY-MM-DD') : '',
          end_date: bond.end_date ? dayjs(bond.end_date).format('YYYY-MM-DD') : '',
          signed_date: bond.signed_date ? dayjs(bond.signed_date).format('YYYY-MM-DD') : '',
          is_renew: bond.is_renew || false,
          status: bond.status || 'ACTIVE',
        });
      } else if (employee) {
        setFormData({
          user_id: employee.id,
          start_date: '',
          end_date: '',
          signed_date: '',
          is_renew: false,
          status: 'ACTIVE',
        });
      }
      setErrors({});
    }
  }, [isOpen, mode, employee, bond]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData?.start_date?.trim()) newErrors.start_date = 'Start date is required';
    if (!formData?.status) newErrors.status = 'Status is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      if (isEdit) {
        await updateBond({ id: bond.id, ...formData }).unwrap();
        toast.success('Bond updated successfully');
        dispatch(usersApi.util.invalidateTags(['Users']));
        onClose();
      } else {
        await addBond(formData).unwrap();
        toast.success('Bond created/renewed successfully');
        dispatch(usersApi.util.invalidateTags(['Users']));
        if (onBondCreated) onBondCreated(formData.user_id);
        onClose();
      }
    } catch (error) {
      console.error('Error saving bond', error);
      toast.error(error?.data?.message || 'Failed to save bond');
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="FileText" size={24} color="var(--color-primary)" />
            <h3 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Edit Bond' : isRenew ? 'Renew Bond' : 'Add Bond'}
            </h3>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Employee Name (display only) */}
          {employee && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
              <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">
                {employee.first_name} {employee.last_name} ({employee.employee_id})
              </div>
            </div>
          )}

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.start_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.start_date && <p className="text-red-500 text-xs mt-1">{errors.start_date}</p>}
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Signed Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Signed Date</label>
            <input
              type="date"
              name="signed_date"
              value={formData.signed_date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.status ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="EXPIRED">Expired</option>
              <option value="BREACHED">Breached</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            {errors.status && <p className="text-red-500 text-xs mt-1">{errors.status}</p>}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isAdding || isUpdating}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
            >
              {isAdding || isUpdating ? (
                <span className="flex items-center">
                  <Icon name="Loader" size={14} className="animate-spin mr-2" />
                  Processing...
                </span>
              ) : isEdit ? (
                'Update'
              ) : isRenew ? (
                'Renew Bond'
              ) : (
                'Add Bond'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBondModal;
